import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { salesOrderService } from '../../../services/salesOrderService';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import TableWithMonthDayGroups from '../../../components/Common/TableWithMonthDayGroups';
import { useLazyMonthList } from '../../../hooks/useLazyMonthList';
import { monthLabelFromPeriod } from '../../../utils/groupByMonthYear';
import { formatMonthIssuedAmounts, formatListStatsAmount } from '../../../utils/documentCurrency';
import VentasCloseShiftModal from '../../../components/Ventas/VentasCloseShiftModal';
import VentasCreateShiftModal from '../../../components/Ventas/VentasCreateShiftModal';
import SalesOrderPdfButton from '../../../components/Ventas/SalesOrderPdfButton';
import { closeShift, reopenShift } from '../../../services/salesShiftClosureService';
import { formatDateLocaleLima, formatDateToLima } from '../../../utils/dateUtils';
import MutateGate from '../../../components/Common/MutateGate';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { POS_CONFIRMED_STATE } from '../../../utils/ventasPosNavigation';
import { getVentasDocumentTypeLabel } from '../../../utils/ventasSalesConstants';
import ListCurrencyBadge from '../../../components/Common/ListCurrencyBadge';
import {
  formatAmountsByCurrency,
  formatMoneyInDocumentCurrency,
  sumIssuedAmountsByCurrency,
} from '../../../utils/documentCurrency';

const VentasOrdersIndex = () => {
  const canMutate = useCanMutate();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [orderToToggle, setOrderToToggle] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [closedShiftDates, setClosedShiftDates] = useState([]);
  const [lastCorrelativeId, setLastCorrelativeId] = useState(null);
  const [closeShiftDay, setCloseShiftDay] = useState(null);
  const [closeShiftLoading, setCloseShiftLoading] = useState(false);
  const [createShiftOpen, setCreateShiftOpen] = useState(false);
  const [reopenShiftTarget, setReopenShiftTarget] = useState(null);
  const [reopenShiftLoading, setReopenShiftLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const buildListParams = useCallback(() => {
    if (!selectedPointOfSale?.id) return {};
    const params = { point_of_sale_id: selectedPointOfSale.id };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [selectedPointOfSale?.id, statusFilter, searchTerm, dateFrom, dateTo]);

  const handleMonthMeta = useCallback((meta) => {
    if (meta?.closed_shift_dates?.length) {
      setClosedShiftDates((prev) => {
        const merged = new Set(prev);
        meta.closed_shift_dates.forEach((d) => merged.add(d));
        return [...merged];
      });
    }
    if (meta?.last_correlative_id != null) {
      setLastCorrelativeId(meta.last_correlative_id);
    }
  }, []);

  const fetchSummary = useCallback((params) => {
    if (!params.point_of_sale_id) {
      return Promise.resolve({ success: true, data: { months: [], stats: null } });
    }
    return salesOrderService.getOrders({ ...params, month_summary: 1 });
  }, []);

  const fetchMonth = useCallback((params) => salesOrderService.getOrders(params), []);

  const {
    monthSummaries,
    listStats,
    loadingSummary,
    loadMonthDetail,
    getMonthItems,
    isMonthLoading,
    reloadSummary,
  } = useLazyMonthList({
    buildParams: buildListParams,
    fetchSummary,
    fetchMonth,
    searchTerm: `${searchTerm} ${dateFrom} ${dateTo}`,
    onMonthMeta: handleMonthMeta,
  });

  useEffect(() => {
    if (location.state?.orderSavedMessage) {
      setToast({ isOpen: true, message: location.state.orderSavedMessage, type: 'success' });
      if (selectedPointOfSale?.id) {
        reloadSummary();
      }
      window.history.replaceState(
        { ...window.history.state, usr: { ...location.state, orderSavedMessage: undefined } },
        '',
      );
    }
  }, [location.state?.orderSavedMessage, selectedPointOfSale?.id, reloadSummary]);

  const isMonthCollapsed = (period) => {
    if (collapsedMonths[period] === undefined) return true;
    return !!collapsedMonths[period];
  };

  const toggleMonth = async (period) => {
    const wasCollapsed = isMonthCollapsed(period);
    if (wasCollapsed) {
      await loadMonthDetail(period);
      setCollapsedMonths((prev) => ({ ...prev, [period]: false }));
      return;
    }
    setCollapsedMonths((prev) => ({ ...prev, [period]: true }));
  };

  const hasExpandedMonth = monthSummaries.some((m) => !isMonthCollapsed(m.period));

  const handleToggleStatusClick = (order) => {
    setOrderToToggle(order);
    setIsToggleModalOpen(true);
  };

  const handleToggleStatusConfirm = async () => {
    if (!orderToToggle) return;
    setLoading(true);
    try {
      const response = await salesOrderService.toggleStatus(orderToToggle.id);
      await reloadSummary();
      setIsToggleModalOpen(false);
      setOrderToToggle(null);
      setToast({
        isOpen: true,
        message: response.message || 'Estado actualizado',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cambiar estado',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    setLoading(true);
    try {
      const response = await salesOrderService.deleteOrder(orderToDelete.id);
      await reloadSummary();
      setIsDeleteModalOpen(false);
      setOrderToDelete(null);
      setToast({
        isOpen: true,
        message: response.message || 'Venta eliminada',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la venta',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const totalOrders = listStats?.total_count ?? 0;
  const issuedOrders = listStats?.issued_count ?? 0;
  const cancelledOrders = listStats?.cancelled_count ?? 0;
  const totalAmountLabel = formatListStatsAmount(listStats);

  const formatRowAmount = (amount, currency) => formatMoneyInDocumentCurrency(amount, currency);

  const formatDayIssuedTotals = (dayGroup) => {
    const byCur = sumIssuedAmountsByCurrency(dayGroup.items);
    return formatAmountsByCurrency(byCur) || formatMoneyInDocumentCurrency(0, 'PEN');
  };

  const isDayClosed = (dayGroup) => {
    const dateKey = dayGroup.date || dayGroup.key;
    return closedShiftDates.includes(dateKey);
  };

  const todayKey = formatDateToLima(new Date());
  const isTodayClosed = closedShiftDates.includes(todayKey);

  const getOrderGuides = (order) => {
    const guides = [];
    const addGuide = (guide, kind) => {
      if (!guide?.id || guides.some((g) => g.id === guide.id)) return;
      guides.push({
        id: guide.id,
        code: guide.code,
        kind,
      });
    };

    (order.exit_guides || []).forEach((guide) => addGuide(guide, 'documento'));
    (order.referenced_exit_guides || []).forEach((guide) => addGuide(guide, 'referencia'));

    return guides;
  };

  const renderGuideLinks = (guides) => {
    if (!guides.length) {
      return <span className="text-gray-400">—</span>;
    }
    return (
      <div className="flex flex-col gap-1">
        {guides.map((guide) => (
          <Link
            key={guide.id}
            to={`/exit-guides/${guide.id}/edit`}
            className="text-sm font-mono text-teal-600 hover:text-teal-900"
            title="Guía de salida del comprobante"
          >
            {guide.code || `GS-${guide.id}`}
          </Link>
        ))}
      </div>
    );
  };

  const handleCloseShiftClick = (dayGroup) => {
    if (isDayClosed(dayGroup)) return;
    setCloseShiftDay(dayGroup);
  };

  const handleCloseShiftConfirm = async ({ shift_date, warehouse_id, exit_time, notes }) => {
    if (!selectedPointOfSale?.id) return;
    setCloseShiftLoading(true);
    try {
      const response = await closeShift({
        point_of_sale_id: selectedPointOfSale.id,
        shift_date,
        warehouse_id,
        exit_time,
        notes,
      });
      if (response.success) {
        setToast({ isOpen: true, message: response.message || 'Turno cerrado', type: 'success' });
        setCloseShiftDay(null);
        await reloadSummary();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo cerrar el turno', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cerrar el turno',
        type: 'error',
      });
    } finally {
      setCloseShiftLoading(false);
    }
  };

  const handleReopenShiftClick = (dateKey, label) => {
    if (!selectedPointOfSale?.id || !dateKey) {
      setToast({ isOpen: true, message: 'No se pudo identificar el turno a reabrir', type: 'error' });
      return;
    }
    setReopenShiftTarget({ dateKey, label });
  };

  const handleCreateShiftConfirm = ({ shift_date }) => {
    if (!shift_date) return;
    if (closedShiftDates.includes(shift_date)) {
      setToast({
        isOpen: true,
        message: 'Ese día ya tiene el turno cerrado. No se pueden crear documentos en una fecha cerrada.',
        type: 'error',
      });
      return;
    }
    setCreateShiftOpen(false);
    navigate('/ventas/orders/create', {
      state: { ...POS_CONFIRMED_STATE, issue_date: shift_date },
    });
  };

  const handleReopenShiftConfirm = async () => {
    if (!reopenShiftTarget?.dateKey || !selectedPointOfSale?.id) return;
    setReopenShiftLoading(true);
    try {
      const response = await reopenShift({
        point_of_sale_id: selectedPointOfSale.id,
        shift_date: reopenShiftTarget.dateKey,
      });
      if (response.success) {
        setToast({ isOpen: true, message: response.message || 'Turno reabierto', type: 'success' });
        setReopenShiftTarget(null);
        await reloadSummary();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo reabrir el turno', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al reabrir el turno',
        type: 'error',
      });
    } finally {
      setReopenShiftLoading(false);
    }
  };

  const handlePdfSuccess = (order) => {
    setToast({
      isOpen: true,
      message: `PDF de ${order.code} descargado`,
      type: 'success',
    });
  };

  const handlePdfError = (message) => {
    setToast({
      isOpen: true,
      message: message || 'Error al generar el PDF',
      type: 'error',
    });
  };

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Ventas</h1>
                <p className="text-gray-600 mt-1">
                  Resumen por mes; al expandir un mes se cargan las ventas de ese periodo, agrupadas por día. Cierre de turno por día.
                </p>
              </div>
              <MutateGate>
                <div className="flex items-center gap-2">
                  {isTodayClosed ? (
                    <span className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-md inline-block">
                      Turno de hoy cerrado
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setCreateShiftOpen(true)}
                    className="px-4 py-2 text-sm font-medium rounded-md bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                  >
                    Crear documentos
                  </button>
                  <Link
                    to="/ventas/orders/bulk-import"
                    className="px-4 py-2 text-sm font-medium rounded-md border border-teal-600 text-teal-700 hover:bg-teal-50 transition-colors"
                  >
                    Importar masiva
                  </Link>
                </div>
              </MutateGate>
            </div>

            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/ventas/clients" className="hover:text-teal-600">Ventas</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Ventas</span>
            </nav>

            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los estados</option>
                  <option value="issued">Emitidas</option>
                  <option value="cancelled">Canceladas</option>
                </select>
                <input
                  type="text"
                  placeholder="Buscar por código o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Total</div>
                  <div className="text-2xl font-bold text-gray-800">{totalOrders}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Emitidas</div>
                  <div className="text-2xl font-bold text-teal-600">{issuedOrders}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Canceladas</div>
                  <div className="text-2xl font-bold text-gray-500">{cancelledOrders}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">Monto total (emitidas)</div>
                  <div className="text-2xl font-bold text-blue-600">{totalAmountLabel}</div>
                </div>
              </div>
            </div>
          </div>

          {!selectedPointOfSale?.id ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
              Selecciona un punto de venta
            </div>
          ) : loadingSummary && monthSummaries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Cargando resumen por mes…</div>
          ) : monthSummaries.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron ventas</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                {hasExpandedMonth && (
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mon.</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ítems</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base imponible</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IGV</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comprobante</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origen</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guía</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                )}
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthSummaries.map((month) => {
                    const period = month.period;
                    const monthCollapsed = isMonthCollapsed(period);
                    const monthLoading = isMonthLoading(period);
                    const monthItems = getMonthItems(period) || [];
                    const count = month.count ?? monthItems.length;
                    const issuedLabel = formatMonthIssuedAmounts(month);

                    return (
                      <React.Fragment key={period}>
                        <tr
                          className="bg-gray-100/80 hover:bg-gray-100 border-y border-gray-200"
                          onClick={() => !monthLoading && toggleMonth(period)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && !monthLoading) {
                              e.preventDefault();
                              toggleMonth(period);
                            }
                          }}
                          aria-expanded={!monthCollapsed}
                        >
                          <td colSpan={13} className="px-6 py-2.5 text-sm font-semibold text-gray-700">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded" aria-hidden>
                                {monthLoading ? (
                                  <span className="inline-block w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                                ) : monthCollapsed ? (
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                )}
                              </span>
                              <span>{monthLabelFromPeriod(period)}</span>
                              <span className="text-gray-500 font-normal">
                                ({count} {count === 1 ? 'registro' : 'registros'})
                              </span>
                              {issuedLabel ? (
                                <span className="text-gray-500 font-normal text-xs">· {issuedLabel}</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                        {!monthCollapsed && monthLoading && (
                          <tr>
                            <td colSpan={13} className="px-6 py-4 text-sm text-gray-500 text-center">
                              Cargando documentos del mes…
                            </td>
                          </tr>
                        )}
                        {!monthCollapsed && !monthLoading && (
                          <TableWithMonthDayGroups
                            embedMode
                            skipMonthHeader
                            data={monthItems}
                            getDateForGrouping={(o) => o.issue_date}
                            defaultCollapsed={false}
                            columnCount={13}
                            emptyMessage="No hay ventas en este mes"
                            renderDaySummary={(dayGroup) => (
                              <span>{`· Total del día: ${formatDayIssuedTotals(dayGroup)}`}</span>
                            )}
                            renderDayActions={(dayGroup) => (
                              <MutateGate>
                                {isDayClosed(dayGroup) ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleReopenShiftClick(dayGroup.date || dayGroup.key, dayGroup.label)
                                    }
                                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-orange-500 text-orange-600 bg-white hover:bg-orange-50 whitespace-nowrap"
                                  >
                                    Abrir turno
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleCloseShiftClick(dayGroup)}
                                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-teal-600 text-teal-700 bg-white hover:bg-teal-50 whitespace-nowrap"
                                  >
                                    Cerrar turno
                                  </button>
                                )}
                              </MutateGate>
                            )}
                            headerRow={null}
                            renderRow={(order) => {
                  const statusLabel = order.status === 'cancelled' ? 'Cancelada' : 'Emitida';
                  const statusClass =
                    order.status === 'cancelled'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800';
                  const orderDateKey = formatDateToLima(order.issue_date);
                  const dayShiftClosed = closedShiftDates.includes(orderDateKey);
                  const canEditOrder =
                    canMutate && order.status !== 'cancelled' && !dayShiftClosed;
                  const canMutateOrderDay = canMutate && !dayShiftClosed;
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link
                          to={`/ventas/orders/${order.id}/edit`}
                          state={POS_CONFIRMED_STATE}
                          className="text-sm font-mono text-teal-600 hover:text-teal-900"
                        >
                          {order.code}
                        </Link>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{order.client?.name || 'N/A'}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateLocaleLima(order.issue_date)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <ListCurrencyBadge currency={order.currency} />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.items?.length || 0} ítems
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatRowAmount(order.base_imponible, order.currency)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatRowAmount(order.igv_amount, order.currency)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatRowAmount(order.total_amount, order.currency)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div>{getVentasDocumentTypeLabel(order.document_type)}</div>
                        {order.series && order.number ? (
                          <div className="text-xs font-mono text-gray-500 mt-0.5">
                            {order.series}-{order.number}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {[
                          ...(order.sales_quotes || []).map((q) => q.code),
                          ...(order.sales_continuous_services || []).map((s) => s.code),
                        ].join(', ') || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {renderGuideLinks(getOrderGuides(order))}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-medium">
                        <div className="flex flex-wrap items-center gap-2">
                          <SalesOrderPdfButton
                            orderId={order.id}
                            onSuccess={handlePdfSuccess}
                            onError={handlePdfError}
                          />
                          <Link
                            to={`/ventas/orders/${order.id}/edit`}
                            state={POS_CONFIRMED_STATE}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            {canEditOrder ? 'Editar' : 'Ver'}
                          </Link>
                          {canMutateOrderDay ? (
                            <MutateGate>
                              <button
                                type="button"
                                onClick={() => handleToggleStatusClick(order)}
                                className={
                                  order.status === 'issued'
                                    ? 'text-orange-600 hover:text-orange-900'
                                    : 'text-green-600 hover:text-green-900'
                                }
                              >
                                {order.status === 'issued' ? 'Anular' : 'Reactivar'}
                              </button>
                              {order.status === 'cancelled' && lastCorrelativeId === order.id ? (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(order)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Eliminar
                                </button>
                              ) : null}
                            </MutateGate>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <VentasCreateShiftModal
        isOpen={createShiftOpen}
        pointOfSale={selectedPointOfSale}
        closedShiftDates={closedShiftDates}
        onClose={() => setCreateShiftOpen(false)}
        onConfirm={handleCreateShiftConfirm}
      />

      <VentasCloseShiftModal
        isOpen={Boolean(closeShiftDay)}
        dayGroup={closeShiftDay}
        pointOfSale={selectedPointOfSale}
        loading={closeShiftLoading}
        onClose={() => !closeShiftLoading && setCloseShiftDay(null)}
        onConfirm={handleCloseShiftConfirm}
      />

      <ConfirmModal
        isOpen={Boolean(reopenShiftTarget)}
        onClose={() => !reopenShiftLoading && setReopenShiftTarget(null)}
        onConfirm={handleReopenShiftConfirm}
        title="Abrir turno"
        message={
          reopenShiftTarget
            ? `¿Abrir el turno de ${reopenShiftTarget.label || reopenShiftTarget.dateKey}? Podrá registrar y modificar ventas de esa fecha. Las guías de salida ya generadas se mantienen; al volver a cerrar solo se generarán guías para las ventas nuevas.`
            : ''
        }
        confirmText="Sí, abrir turno"
        cancelText="No"
        isLoading={reopenShiftLoading}
      />

      <ConfirmModal
        isOpen={isToggleModalOpen}
        onClose={() => {
          setIsToggleModalOpen(false);
          setOrderToToggle(null);
        }}
        onConfirm={handleToggleStatusConfirm}
        title={orderToToggle?.status === 'issued' ? 'Anular venta' : 'Reactivar venta'}
        message={
          orderToToggle
            ? orderToToggle.status === 'issued'
              ? `¿Anular la venta "${orderToToggle.code}"? Las guías de salida asociadas quedarán pendientes y se revertirá el stock.`
              : `¿Reactivar la venta "${orderToToggle.code}"? Al cerrar el turno se generará de nuevo la guía de salida si corresponde.`
            : ''
        }
        confirmText="Sí"
        cancelText="No"
        isLoading={loading}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setOrderToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar venta"
        message={
          orderToDelete
            ? `¿Eliminar definitivamente la venta "${orderToDelete.code}"? Esta acción libera el correlativo para usarlo de nuevo.`
            : ''
        }
        confirmText="Sí, eliminar"
        cancelText="No"
        isLoading={loading}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default VentasOrdersIndex;
